import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { StatsCards } from '../../components/StatsCards';
import { FilterBar } from '../../components/FilterBar';
import api from '../../config/api';
import { User } from '../../services/authService';
import { userService } from '../../services/userService';

const SECTION_DETAILS: Record<'VCN' | 'HDR' | 'SANTE', { label: string; icon: string; badgeClass: string }> = {
  VCN: { label: 'VCN (Université)', icon: '🎓', badgeClass: 'bg-blue-100 text-blue-800' },
  HDR: { label: 'HDR', icon: '🏛️', badgeClass: 'bg-purple-100 text-purple-800' },
  SANTE: { label: 'SANTÉ', icon: '🩺', badgeClass: 'bg-emerald-100 text-emerald-800' },
};

export const AdminUsers: React.FC = () => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    actifs: 0,
    inactifs: 0,
    admins: 0,
    membres: 0,
    visiteurs: 0,
    sections: {
      VCN: 0,
      HDR: 0,
      SANTE: 0,
    },
  });
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    section: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers(1);
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allUsers, currentPage]);

  const fetchStats = async () => {
    try {
      const allData = await userService.getAll({ page_size: 1000 });
      const allUsers = allData.results || [];
      
      const statsData = {
        total: allData.count || allUsers.length,
        actifs: allUsers.filter((u: User) => u.is_active).length,
        inactifs: allUsers.filter((u: User) => !u.is_active).length,
        admins: allUsers.filter((u: User) => u.role === 'admin').length,
        membres: allUsers.filter((u: User) => u.role === 'membre').length,
        visiteurs: allUsers.filter((u: User) => u.role === 'visiteur').length,
        sections: {
          VCN: allUsers.filter((u: User) => u.section === 'VCN').length,
          HDR: allUsers.filter((u: User) => u.section === 'HDR').length,
          SANTE: allUsers.filter((u: User) => u.section === 'SANTE').length,
        },
      };
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const fetchUsers = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await userService.getAll({ page_size: 1000 });
      const allUsersData = data.results || data || [];
      setAllUsers(allUsersData);
      setTotalCount(allUsersData.length);
      setErrorMsg('');
    } catch (error: any) {
      console.error('Erreur:', error);
      if (error?.response?.status === 403) {
        setErrorMsg("Accès interdit. Veuillez vous connecter en tant qu'administrateur.");
      } else {
        setErrorMsg('Impossible de charger les utilisateurs.');
      }
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allUsers];

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((user) =>
        user.username?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par rôle
    if (filters.role) {
      filtered = filtered.filter((user) => user.role === filters.role);
    }

    // Filtre par statut
    if (filters.status) {
      const isActive = filters.status === 'active';
      filtered = filtered.filter((user) => (user.is_active ?? true) === isActive);
    }

    // Filtre par section
    if (filters.section) {
      filtered = filtered.filter((user) => user.section === filters.section);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date(a.date_joined || 0).getTime();
      const dateB = new Date(b.date_joined || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 6;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedUsers = filtered.slice(startIndex, endIndex);

    setUsers(paginatedUsers);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleView = (user: User) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      role: user.role,
      is_active: user.is_active ?? true,
      section: user.section || 'VCN',
    });
    setIsModalOpen(true);
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleToggleActive = async (user: User) => {
    try {
      const newStatus = !(user.is_active ?? true);
      await userService.update(user.id, { is_active: newStatus } as any);
      fetchUsers(currentPage);
      fetchStats();
      setIsViewModalOpen(false);
    } catch (error: any) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data 
        ? (typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data))
        : 'Une erreur est survenue';
      alert(`Erreur: ${errorMessage}`);
    }
  };

  const handleChangeRole = async (user: User, newRole: string) => {
    try {
      await userService.update(user.id, { role: newRole });
      fetchUsers(currentPage);
      fetchStats();
      setIsViewModalOpen(false);
    } catch (error: any) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data 
        ? (typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data))
        : 'Une erreur est survenue';
      alert(`Erreur: ${errorMessage}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await userService.update(selectedUser.id, formData);
      setIsModalOpen(false);
      fetchUsers(currentPage);
      fetchStats();
    } catch (error: any) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data 
        ? (typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data))
        : 'Une erreur est survenue lors de la mise à jour';
      alert(`Erreur: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;
    try {
      await userService.delete(selectedUser.id);
      setIsDeleteModalOpen(false);
      fetchUsers(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'username', 
      label: 'Utilisateur',
      render: (value: string, row: User) => (
        <div>
          <strong className="text-green-700">{value}</strong>
          <p className="text-xs text-gray-500">{row.email}</p>
        </div>
      )
    },
    {
      key: 'first_name',
      label: 'Nom complet',
      render: (_: any, row: User) => (
        <span className="font-medium">
          {row.first_name && row.last_name 
            ? `${row.first_name} ${row.last_name}`
            : row.username}
        </span>
      )
    },
    {
      key: 'role',
      label: 'Rôle',
      render: (value: string) => {
        const roles: { [key: string]: { label: string; color: string } } = {
          admin: { label: 'Admin', color: 'bg-red-100 text-red-800' },
          membre: { label: 'Membre', color: 'bg-blue-100 text-blue-800' },
          visiteur: { label: 'Visiteur', color: 'bg-gray-100 text-gray-800' },
        };
        const role = roles[value] || { label: value, color: 'bg-gray-100 text-gray-800' };
        return (
          <span className={`${role.color} px-2 py-1 rounded-full text-xs font-semibold`}>
            {role.label}
          </span>
        );
      }
    },
    {
      key: 'section',
      label: 'Section',
      render: (value: string | undefined) => {
        const sectionKey = (value as 'VCN' | 'HDR' | 'SANTE') || 'VCN';
        const section = SECTION_DETAILS[sectionKey] || SECTION_DETAILS.VCN;
        return (
          <span className={`${section.badgeClass} px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1`}>
            <span>{section.icon}</span>
            <span>{section.label.split(' ')[0]}</span>
          </span>
        );
      }
    },
    {
      key: 'is_active',
      label: 'Statut',
      render: (value: boolean | undefined) => {
        const isActive = value ?? true;
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
            isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {isActive ? 'Actif' : 'Inactif'}
          </span>
        );
      }
    },
    {
      key: 'date_joined',
      label: 'Date d\'inscription',
      render: (value: string) => (
        <span className="text-gray-600 text-sm">
          {new Date(value).toLocaleDateString('fr-FR')}
        </span>
      )
    },
  ];

  if (loading) {
    return (
      <AdminLayout activeSection="users">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="users">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-green-700">Gestion des Utilisateurs</h2>
        <p className="text-gray-600 mt-1">Gérez tous les utilisateurs de la plateforme</p>
      </div>

      {errorMsg && (
        <div className="mb-4 bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <p className="text-red-700 text-sm font-medium">{errorMsg}</p>
        </div>
      )}

      {/* Cartes de statistiques */}
      <StatsCards
        stats={[
          { label: 'Total Utilisateurs', value: stats.total, icon: '👥', color: 'blue' },
          { label: 'Actifs', value: stats.actifs, icon: '✅', color: 'green' },
          { label: 'Inactifs', value: stats.inactifs, icon: '❌', color: 'red' },
          { label: 'Admins', value: stats.admins, icon: '👑', color: 'purple' },
          { label: 'Membres', value: stats.membres, icon: '👤', color: 'indigo' },
          { label: 'Visiteurs', value: stats.visiteurs, icon: '🌐', color: 'teal' },
        ]}
      />
      <StatsCards
        stats={[
          { label: 'Section VCN', value: stats.sections.VCN, icon: '🎓', color: 'blue' },
          { label: 'Section HDR', value: stats.sections.HDR, icon: '🏛️', color: 'purple' },
          { label: 'Section SANTÉ', value: stats.sections.SANTE, icon: '🩺', color: 'teal' },
        ]}
      />

      {/* Filtres */}
      <FilterBar
        searchPlaceholder="🔍 Rechercher par nom, email, username..."
        searchValue={filters.search}
        onSearchChange={(value) => {
          setFilters({ ...filters, search: value });
          setCurrentPage(1);
        }}
        filters={[
          {
            key: 'role',
            label: 'Tous les rôles',
            value: filters.role,
            onChange: (value) => {
              setFilters({ ...filters, role: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'admin', label: '👑 Admin' },
              { value: 'membre', label: '👤 Membre' },
              { value: 'visiteur', label: '🌐 Visiteur' },
            ],
          },
          {
            key: 'status',
            label: 'Tous les statuts',
            value: filters.status,
            onChange: (value) => {
              setFilters({ ...filters, status: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'active', label: '✅ Actifs' },
              { value: 'inactive', label: '❌ Inactifs' },
            ],
          },
          {
            key: 'section',
            label: 'Toutes les sections',
            value: filters.section,
            onChange: (value) => {
              setFilters({ ...filters, section: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'VCN', label: '🎓 VCN (Université)' },
              { value: 'HDR', label: '🏛️ HDR' },
              { value: 'SANTE', label: '🩺 SANTÉ' },
            ],
          },
        ]}
        onClear={() => {
          setFilters({ search: '', role: '', status: '', section: '' });
          setCurrentPage(1);
        }}
      />

      <Table
        columns={columns}
        data={users}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        loading={loading}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />

      {/* Modal Détails */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Détails de l'utilisateur"
        footer={
          selectedUser && (
            <div className="flex gap-4">
              <button
                onClick={() => handleToggleActive(selectedUser)}
                className={`${(selectedUser.is_active ?? true) ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors`}
              >
                {(selectedUser.is_active ?? true) ? 'Désactiver' : 'Activer'}
              </button>
              {selectedUser.role !== 'admin' && (
                <select
                  value={selectedUser.role}
                  onChange={(e) => handleChangeRole(selectedUser, e.target.value)}
                  className="input-field"
                >
                  <option value="membre">Membre</option>
                  <option value="admin">Admin</option>
                  <option value="visiteur">Visiteur</option>
                </select>
              )}
            </div>
          )
        }
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Nom d'utilisateur</p>
                <p className="font-semibold text-lg">{selectedUser.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{selectedUser.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Prénom</p>
                <p className="font-semibold">{selectedUser.first_name || 'Non renseigné'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Nom</p>
                <p className="font-semibold">{selectedUser.last_name || 'Non renseigné'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Téléphone</p>
                <p className="font-semibold">{selectedUser.phone || 'Non renseigné'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Rôle</p>
                <p className="font-semibold">{selectedUser.role}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Section</p>
              <p className="font-semibold inline-flex items-center gap-2">
                <span>
                  {SECTION_DETAILS[(selectedUser.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].icon}
                </span>
                <span>
                  {SECTION_DETAILS[(selectedUser.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].label}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Date d'inscription</p>
              <p className="font-semibold">{new Date(selectedUser.date_joined).toLocaleDateString('fr-FR')}</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Modifier l'utilisateur"
        size="lg"
        footer={
          <div className="flex gap-4">
            <button 
              onClick={() => setIsModalOpen(false)} 
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button 
              onClick={handleSubmit} 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom d'utilisateur *</label>
              <input
                type="text"
                className="input-field"
                value={formData.username || ''}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                className="input-field"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
              <input
                type="text"
                className="input-field"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
              <input
                type="text"
                className="input-field"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
              <input
                type="text"
                className="input-field"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rôle *</label>
              <select
                className="input-field"
                value={formData.role || ''}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              >
                <option value="membre">Membre</option>
                <option value="admin">Admin</option>
                <option value="visiteur">Visiteur</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section *</label>
            <select
              className="input-field"
              value={formData.section || 'VCN'}
              onChange={(e) => setFormData({ ...formData, section: e.target.value })}
              required
            >
              <option value="VCN">🎓 VCN (Université)</option>
              <option value="HDR">🏛️ HDR</option>
              <option value="SANTE">🩺 SANTÉ</option>
            </select>
          </div>
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_active ?? true}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700">Actif</span>
            </label>
          </div>
        </form>
      </Modal>

      {/* Modal Suppression */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsDeleteModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={confirmDelete} className="btn-danger">
              Supprimer
            </button>
          </div>
        }
      >
        <p className="text-lg">
          Êtes-vous sûr de vouloir supprimer l'utilisateur{' '}
          <strong>"{selectedUser?.username}"</strong> ?
        </p>
        <p className="text-sm text-gray-600 mt-2">
          Cette action est irréversible.
        </p>
      </Modal>
    </AdminLayout>
  );
};

